import { buildApiUrl } from "@/lib/apiBase";

export const ELRA_CERTIFICATE_VERIFICATION_API = buildApiUrl("/api/elra/verify-certificate");
export const ELRA_CERTIFICATE_VERIFICATION_FRONTEND = "https://app.elra.gov.ng/auth/verify-certificate";
export const ELRA_EQUIPMENT_REGISTRY_VERIFICATION_API = buildApiUrl("/api/elra/equipment-registry/verify");

interface ElraApiPayload {
  id?: number;
  certificate_number?: string;
  verification_link?: string;
  issued_date?: string;
  expiry_date?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  is_expired?: boolean;
  is_expiring_soon?: boolean;
  days_until_expiry?: number;
  validity_period_days?: number;
  certificate_url?: string;
  verification_url?: string;
  message?: string;
}

interface EquipmentRegistryApiPayload {
  success?: boolean;
  message?: string;
  data?: {
    id?: number;
    unique_identifier?: string;
    user_id?: number;
    status?: string;
    status_label?: string;
    step?: string;
    step_label?: string;
    equipment_type?: string;
    equipment_name?: string;
    equipment_category_id?: number | null;
    equipment_category?: string | null;
    make?: string;
    model?: string;
    year_of_manufacture?: number;
    asset_value?: number;
    serial_number?: string;
    description?: string;
    condition?: string;
    location?: string;
    origin_address?: string | null;
    registered_business_address?: string;
    insurance_company?: string;
    policy_number?: string;
    insurance_start_date?: string | null;
    insurance_expiry_date?: string | null;
    warranty_provider?: string;
    warranty_expiry_date?: string | null;
    payment_entry_id?: number | null;
    submitted_at?: string;
    admin_notes?: string;
    registration_fee_paid_at?: string | null;
    last_renewal_paid_at?: string | null;
    fee_preview?: {
      band?: string;
      band_label?: string;
      registration_fee?: number;
      renewal_fee?: number;
    };
    created_at?: string;
    updated_at?: string;
    qr_code?: string;
    documents?: Array<{
      id?: number;
      updatable_id?: number;
      updatable_type?: string;
      document_code?: string;
      document_type?: string;
      document_type_label?: string;
      mime_type?: string;
      verification_status?: string;
      verification_notes?: string | null;
      expiry_date?: string | null;
      is_required?: boolean;
      file_url?: string;
      created_at?: string;
      updated_at?: string;
    }>;
  } | null;
}

export interface CertificateVerificationResponse {
  verified: boolean;
  status: "Verified" | "Not Verified";
  message: string;
  checkedAt: string;
  source: "api" | "offline";
  certificateNumber?: string;
  verificationUrl?: string;
  expiryDate?: string;
}

export interface EquipmentRegistryVerificationResponse {
  verified: boolean;
  status: "Verified" | "Not Verified";
  message: string;
  checkedAt: string;
  registryId: string;
  source: "api" | "offline";
  details?: {
    id?: number;
    uniqueIdentifier: string;
    status?: string;
    statusLabel?: string;
    step?: string;
    stepLabel?: string;
    equipmentType?: string;
    equipmentName?: string;
    make?: string;
    model?: string;
    yearOfManufacture?: number;
    assetValue?: number;
    serialNumber?: string;
    description?: string;
    condition?: string;
    location?: string;
    registeredBusinessAddress?: string;
    adminNotes?: string;
    submittedAt?: string;
    updatedAt?: string;
    qrCode?: string;
    feePreview?: {
      band?: string;
      bandLabel?: string;
      registrationFee?: number;
      renewalFee?: number;
    };
    documents: Array<{
      id?: number;
      documentCode?: string;
      documentType?: string;
      documentTypeLabel?: string;
      mimeType?: string;
      verificationStatus?: string;
      verificationNotes?: string | null;
      isRequired?: boolean;
      fileUrl?: string;
      createdAt?: string;
      updatedAt?: string;
    }>;
  };
}

const normalizeEquipmentRegistryDetails = (payload: EquipmentRegistryApiPayload["data"], normalizedId: string) => {
  if (!payload) return undefined;

  return {
    id: payload.id,
    uniqueIdentifier: payload.unique_identifier || normalizedId,
    status: payload.status,
    statusLabel: payload.status_label,
    step: payload.step,
    stepLabel: payload.step_label,
    equipmentType: payload.equipment_type,
    equipmentName: payload.equipment_name,
    make: payload.make,
    model: payload.model,
    yearOfManufacture: payload.year_of_manufacture,
    assetValue: payload.asset_value,
    serialNumber: payload.serial_number,
    description: payload.description,
    condition: payload.condition,
    location: payload.location,
    registeredBusinessAddress: payload.registered_business_address,
    adminNotes: payload.admin_notes,
    submittedAt: payload.submitted_at,
    updatedAt: payload.updated_at,
    qrCode: payload.qr_code,
    feePreview: payload.fee_preview
      ? {
          band: payload.fee_preview.band,
          bandLabel: payload.fee_preview.band_label,
          registrationFee: payload.fee_preview.registration_fee,
          renewalFee: payload.fee_preview.renewal_fee,
        }
      : undefined,
    documents: (payload.documents || []).map((document) => ({
      id: document.id,
      documentCode: document.document_code,
      documentType: document.document_type,
      documentTypeLabel: document.document_type_label,
      mimeType: document.mime_type,
      verificationStatus: document.verification_status,
      verificationNotes: document.verification_notes,
      isRequired: document.is_required,
      fileUrl: document.file_url,
      createdAt: document.created_at,
      updatedAt: document.updated_at,
    })),
  };
};

export const verifyEquipmentRegistryId = async (registryId: string): Promise<EquipmentRegistryVerificationResponse> => {
  const normalizedId = registryId.trim().toUpperCase();

  if (!normalizedId) {
    return {
      verified: false,
      status: "Not Verified",
      message: "Please enter a valid equipment registry ID.",
      checkedAt: new Date().toISOString(),
      registryId: "",
      source: "offline",
    };
  }

  try {
    const response = await fetch(`${ELRA_EQUIPMENT_REGISTRY_VERIFICATION_API}/${encodeURIComponent(normalizedId)}`);
    if (!response.ok) {
      throw new Error("Equipment registry verification API is not reachable.");
    }

    const payload = (await response.json()) as EquipmentRegistryApiPayload;
    const details = normalizeEquipmentRegistryDetails(payload?.data, normalizedId);
    const approvedStatus = String(payload?.data?.status || "").toLowerCase() === "approved";
    const verified = Boolean(payload?.success && payload?.data && approvedStatus);

    return {
      verified,
      status: verified ? "Verified" : "Not Verified",
      message: payload?.message || (verified
        ? `Equipment registry ID ${normalizedId} has been verified successfully.`
        : `Equipment registry ID ${normalizedId} could not be verified.`),
      checkedAt: new Date().toISOString(),
      registryId: normalizedId,
      source: "api",
      details,
    };
  } catch {
    return {
      verified: false,
      status: "Not Verified",
      message: `Equipment registry verification is unavailable right now for ${normalizedId}. Please retry when backend connectivity is restored.`,
      checkedAt: new Date().toISOString(),
      registryId: normalizedId,
      source: "offline",
      details: undefined,
    };
  }
};

export const verifyCertificateId = async (certificateId: string): Promise<CertificateVerificationResponse> => {
  const suffix = certificateId.trim().toUpperCase().replace(/^LIC-/i, "");
  const normalizedId = suffix ? `LIC-${suffix}` : "";

  if (!normalizedId) {
    return {
      verified: false,
      status: "Not Verified",
      message: "Please enter a valid ELRA certificate ID.",
      checkedAt: new Date().toISOString(),
      source: "offline",
    };
  }

  try {
    const response = await fetch(ELRA_CERTIFICATE_VERIFICATION_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ certificate_number: normalizedId }),
    });

    if (!response.ok) {
      throw new Error("ELRA certificate verification API is not reachable.");
    }

    const payload = (await response.json()) as ElraApiPayload;
    const verified = Boolean(payload?.certificate_number && payload?.status === "active" && !payload?.is_expired);

    return {
      verified,
      status: verified ? "Verified" : "Not Verified",
      message: verified
        ? `Certificate ${payload?.certificate_number ?? normalizedId} is active until ${payload?.expiry_date ?? "the stated expiry date"}.`
        : `Certificate ${normalizedId} is not valid or has expired.`,
      checkedAt: new Date().toISOString(),
      source: "api",
      certificateNumber: payload?.certificate_number ?? normalizedId,
      verificationUrl: payload?.verification_url ?? payload?.verification_link ?? `${ELRA_CERTIFICATE_VERIFICATION_FRONTEND}/${normalizedId}`,
      expiryDate: payload?.expiry_date,
    };
  } catch {
    return {
      verified: false,
      status: "Not Verified",
      message: `ELRA certificate verification is unavailable right now for ${normalizedId}. Please retry when backend connectivity is restored.`,
      checkedAt: new Date().toISOString(),
      source: "offline",
      certificateNumber: normalizedId,
      verificationUrl: `${ELRA_CERTIFICATE_VERIFICATION_FRONTEND}/${normalizedId}`,
      expiryDate: undefined,
    };
  }
};
